import { useEffect, useState } from "react";
import { useCuttingPlanStore } from "../../stores/cuttingPlanStore"
import { checkProjectActivationOrPermission } from "../../other/utilFunctions";

function showPattern(pattern, checkedFloors, checkedDiameters, showLowerFloorPatterns) {
    const patternFloors = getFloorsOfAPattern(pattern);
    const currentMinFloor = Math.min(...checkedFloors);
    const patternMinFloor = Math.min(...patternFloors);

    if (showLowerFloorPatterns) {
        return (
            checkedDiameters.includes(pattern.diameter)
            && patternFloors.some(floor => checkedFloors.includes(floor))
        );
    }
    else {
        return (
            checkedDiameters.includes(pattern.diameter)
            && patternFloors.some(floor => checkedFloors.includes(floor))
            && patternMinFloor >= currentMinFloor
        );
    }
}

function getFloorsOfAPattern(pattern) {
    const floorsOfCuts = pattern.cuts.map(cut => cut.floors).flat().map(floor => parseInt(floor));
    return [...new Set(floorsOfCuts)];
}

const useRebarCpInfoNew = (result, userType) => {

    const [isComponentLoading, setIsComponentLoading] = useState(true)

    const setLogLength = useCuttingPlanStore(state => state.setLogLength);

    const setAllPatterns = useCuttingPlanStore(state => state.setAllPatterns);
    const allPatterns = useCuttingPlanStore(state => state.allPatterns);
    const setAllDiameters = useCuttingPlanStore(state => state.setAllDiameters);
    const setAllFloors = useCuttingPlanStore(state => state.setAllFloors);

    const setCheckedDiameters = useCuttingPlanStore(state => state.setCheckedDiameters);
    const checkedDiameters = useCuttingPlanStore(state => state.checkedDiameters);
    const setCheckedFloors = useCuttingPlanStore(state => state.setCheckedFloors);
    const checkedFloors = useCuttingPlanStore(state => state.checkedFloors);

    const setCuttingPlanInformation = useCuttingPlanStore(state => state.setCuttingPlanInformation);

    const setRebarInformation = useCuttingPlanStore(state => state.setRebarInformation);

    const setOverlapRatio = useCuttingPlanStore(state => state.setOverlapRatio);

    const setMinOutageLength = useCuttingPlanStore(state => state.setMinOutageLength);

    const showLowerFloorPatterns = useCuttingPlanStore(state => state.showLowerFloorPatterns);

    const setProjectActivated = useCuttingPlanStore(state => state.setProjectActivated);

    const setProjectCuttingPlanPermission = useCuttingPlanStore(state => state.setProjectCuttingPlanPermission);

    let diameterResultKeys;
    let diameterCheckboxValues;
    const activationOrPermission = checkProjectActivationOrPermission(result.activated, result.cuttingPlanPermission)
    if (result.backgroundTask || !activationOrPermission) {
        diameterResultKeys = [];
        diameterCheckboxValues = [];
    }
    else {
        diameterResultKeys = Object.keys(result.diameterResults).sort((a, b) => b - a);
        diameterCheckboxValues = diameterResultKeys.map((item) => (
            { label: `Φ${item}`, value: item }
        ));
    }

    const rawSelectedFloors = result.selectedFloors
    const allFloors = result.allFloors

    let _floors;

    if (result.backgroundTask || !activationOrPermission) {
        _floors = [];
    }
    else {
        _floors = allFloors.map(item => {
            return { "label": item, "value": allFloors.indexOf(item) }
        })
    }

    let allPatternsObject;

    if (result.backgroundTask || !activationOrPermission) {
        allPatternsObject = [];
    }
    else {
        allPatternsObject = Object.entries(result.diameterResults)
            .map(([diameter, diameterDetails]) => diameterDetails.patterns.map(pattern => {
                const newPattern = {
                    ...pattern,
                    diameter: diameter,
                    floors: getFloorsOfAPattern(pattern),
                    preCut: false
                }

                return {
                    ...newPattern,
                    show: showPattern(newPattern, [_floors[0].value], [diameterResultKeys[0]], showLowerFloorPatterns)
                }
            })).flat();
    }

    const [firstRun, setFirstRun] = useState(true);

    useEffect(() => {
        setProjectActivated(result.activated);
        setProjectCuttingPlanPermission(result.projectCuttingPlanPermission);

        if (result.backgroundTask || !activationOrPermission) {
            setLogLength(result.logLength)
            setAllFloors(result.floors)
            setAllDiameters(result.diameters)
            setIsComponentLoading(false);
            return;
        }

        setAllDiameters(diameterCheckboxValues);
        setCheckedDiameters([diameterResultKeys[0]]);

        setAllFloors(_floors)
        setCheckedFloors([_floors[0].value])

        setAllPatterns(allPatternsObject);


        let allRebarCodes = []
        allPatternsObject.forEach(pattern => {
            allRebarCodes.push(...Object.keys(pattern.elements))
            if ("substitutions"in pattern) {
                allRebarCodes.push(...pattern.substitutions.map(sub => sub.actual))
            }
        })
        allRebarCodes = [...new Set(allRebarCodes)]

        const rebarInformationOfPatterns = Object.values(result.donatiDict)
            .filter(rebar => allRebarCodes.includes(rebar.donatiKodu))
            .reduce((acc, rebar) => {
                acc[rebar.donatiKodu] = result.donatiDict[rebar.donatiKodu];
                return acc;
            }, {});

        setRebarInformation(rebarInformationOfPatterns);

        setOverlapRatio(result.bindirmeOrani);

        setMinOutageLength(result.minOutageLength);

        setCuttingPlanInformation({
            cpName: result.cpName,
            projectId: result.projectId,
            projectName: result.projectName,
            projectUser: `${result.projectUser.email} (${result.projectUser.id})`,
            floors: result.allFloors.join(", "),
            diameters: diameterResultKeys.map(d => `Φ${d}`).join(", "),
            projectOverlapRatio: result.bindirmeOrani,
            minOutageLength: result.minOutageLength,
            solveFloorsSeparately: result.solveFloorsSeparately,
            creationTime: new Date(result.creationTime),
            totalExecutionTime: Object.values(result.diameterResults).reduce((acc, cur) => acc + cur.executionTime, 0),
            diameterOptions: Object.entries(result.diameterResults).map(diameterResult => ({
                diameter: `Φ${diameterResult[0]}`,
                executionTime: diameterResult[1].executionTime,
                usePartitions: diameterResult[1].use_partitions,
                useSubstitutes: diameterResult[1].useSubstitutes
            })),
            useUnoptimizedSolver: result.useUnoptimizedSolver
        })

        setFirstRun(false);
        setIsComponentLoading(false);
    }, []);

    useEffect(() => {
        if (result.backgroundTask || !activationOrPermission) {
            return;
        }

        if (firstRun) {
            return;
        }

        setAllPatterns(
            allPatterns.map(pattern => ({
                ...pattern,
                show: showPattern(pattern, checkedFloors, checkedDiameters, showLowerFloorPatterns)
            }))
        );

    }, [checkedDiameters, checkedFloors, showLowerFloorPatterns]);

    return {
        isComponentLoading
    }
}

export default useRebarCpInfoNew;